export const namespaced = true

const STORAGE_KEY = 'apropos_cookie_consent_v1'
const CONSENT_VERSION = 1

const CATEGORY_KEYS = ['essential', 'preferences', 'statistics', 'marketing']

function getDefaultCategories() {
	return {
		essential: true,
		preferences: false,
		statistics: false,
		marketing: false
	}
}

function createConsent({ givenAt = null, categories = {} } = {}) {
	return {
		version: CONSENT_VERSION,
		givenAt,
		categories: {
			...getDefaultCategories(),
			...categories,
			essential: true
		}
	}
}

function readFromStorage() {
	if (!process.client) {
		return null
	}

	const raw = window.localStorage.getItem(STORAGE_KEY)
	if (!raw) {
		return null
	}

	try {
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') {
			return null
		}

		if (parsed.version !== CONSENT_VERSION) {
			return null
		}

		return createConsent({
			givenAt: typeof parsed.givenAt === 'string' ? parsed.givenAt : null,
			categories: parsed.categories || {}
		})
	} catch (e) {
		return null
	}
}

function writeToStorage(consent) {
	if (!process.client) {
		return false
	}

	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
		return true
	} catch (e) {
		if (process.env.DEPLOY_ENV !== 'production') {
			console.warn('Failed to write cookie consent to localStorage:', e)
		}
		return false
	}
}

export const state = () => ({
	consent: null
})

export const getters = {
	hasAnswered(state) {
		return Boolean(state.consent && state.consent.givenAt)
	},

	categoryEnabled: state => category => {
		if (!CATEGORY_KEYS.includes(category)) {
			return false
		}

		const consent = state.consent || createConsent({ givenAt: null })
		return Boolean(consent.categories && consent.categories[category])
	}
}

export const mutations = {
	setConsent(state, consent) {
		state.consent = consent
	}
}

export const actions = {
	initFromStorage({ commit }) {
		const stored = readFromStorage()
		if (stored) {
			commit('setConsent', stored)
			return stored
		}

		const defaultConsent = createConsent({ givenAt: null })
		commit('setConsent', defaultConsent)
		return defaultConsent
	},

	acceptAll({ commit }) {
		const consent = createConsent({
			givenAt: new Date().toISOString(),
			categories: {
				essential: true,
				preferences: true,
				statistics: true,
				marketing: true
			}
		})

		writeToStorage(consent)
		commit('setConsent', consent)
		return consent
	},

	rejectAll({ commit }) {
		const consent = createConsent({
			givenAt: new Date().toISOString(),
			categories: {
				essential: true,
				preferences: false,
				statistics: false,
				marketing: false
			}
		})

		writeToStorage(consent)
		commit('setConsent', consent)
		return consent
	},

	saveCustom({ commit }, payload) {
		const categories = payload && payload.categories ? payload.categories : payload

		const consent = createConsent({
			givenAt: new Date().toISOString(),
			categories: categories || {}
		})

		writeToStorage(consent)
		commit('setConsent', consent)
		return consent
	}
}
